// ============================================================================
// TRAINING TABLE SERVICE
// ============================================================================
// Manages training tables - creation, joining, leaving, and bot management.
// Training tables are private (invite-only) and use PlayCoins only.
// ============================================================================

import { db } from '../../firebase';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  addDoc,
  query,
  where,
  onSnapshot,
  Timestamp,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { BotPlayer, BotDifficulty, createBots, createBot } from './trainingBotService';
import { trainingDecisionLogger, TrainingSession } from './trainingDecisionLogger';
import { analyzeTrainingSession, TrainingReport } from './trainingAnalysisService';
import { saveTrainingReport } from './trainingReportService';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export interface TrainingTableConfig {
  tableName: string;
  hostId: string;
  hostName: string;
  
  // Blinds
  smallBlind: number;
  bigBlind: number;
  
  // Stack
  startingStack: number;
  
  // Bots
  botCount: number;
  botDifficulty: BotDifficulty | 'mixed';
  
  // Settings
  maxPlayers: number; // Usually 8
  autoStart: boolean; // Start when host joins

  // 🏆 Tournament-training mini-game (optional): asymmetric stacks + a chip
  // goal. When present, startTrainingGame uses heroChips/botChips instead of
  // startingStack, and TrainingGamePage shows the goal HUD + completion flow.
  challenge?: TrainingChallengeConfig;
}

export interface TrainingChallengeConfig {
  id: string;              // tournament challenge id (reward claim key)
  title: string;
  heroChips: number;
  botChips: number;
  goalType: 'chips' | 'preflopWins' | 'survive';
  goalChips: number;       // chips target ('chips'/'survive')
  goalCount?: number;      // preflop pots to win ('preflopWins')
  maxHands: number;
  rewardPkCash: number;
  mission: string;         // one-line mission statement (briefing + HUD)
  tips: string[];          // strategy tips for the pre-game briefing
  skill?: string;          // the tournament skill this challenge trains
  concept?: string;        // concept key - drives the briefing's Key Terms glossary panel
}

export interface TrainingTable {
  tableId: string;
  tableName: string;
  
  // Host info
  hostId: string;
  hostName: string;
  
  // Status
  status: 'waiting' | 'playing' | 'ended';
  isPrivate: true; // Training tables are always private
  currency: 'playcoins'; // Training only uses PlayCoins
  
  // Config
  smallBlind: number;
  bigBlind: number;
  startingStack: number;
  maxPlayers: number;
  
  // Bots
  botDifficulty: BotDifficulty | 'mixed';
  bots: BotPlayer[];
  
  // Players (humans)
  players: string[]; // uids
  playerNames: Record<string, string>;
  
  // Invite system
  inviteCode: string;
  invitedPlayers: string[]; // uids who can join
  
  // Timestamps
  createdAt: Timestamp;
  updatedAt: Timestamp;
  
  // Game state reference
  gameStateId?: string;
  gameState?: any; // GameState from game engine

  // 🏆 Mini-game challenge config (see TrainingChallengeConfig)
  challenge?: TrainingChallengeConfig;
}

export interface TrainingTablePlayer {
  uid: string;
  username: string;
  isBot: boolean;
  botDifficulty?: BotDifficulty;
  seatIndex: number;
  chips: number;
  isActive: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const TRAINING_TABLES_COLLECTION = 'trainingTables';

// Default config values
const DEFAULT_CONFIG = {
  smallBlind: 1,
  bigBlind: 2,
  startingStack: 200, // 100 big blinds
  maxPlayers: 8,
};

// ============================================================================
// TABLE CREATION
// ============================================================================

/**
 * Generate a unique invite code
 */
function generateInviteCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

/**
 * Create a new training table
 */
export async function createTrainingTable(config: TrainingTableConfig): Promise<TrainingTable> {
  try {
    console.log(`🎰 [TrainingTable] Creating table for ${config.hostName}`);
    
    // Generate table ID and invite code
    const tableId = `training_${Date.now()}_${Math.random().toString(36).substr(2, 8)}`;
    const inviteCode = generateInviteCode();
    
    // Create bots
    const bots = createBots(config.botCount, config.botDifficulty);
    console.log(`   🤖 Created ${bots.length} bots (${config.botDifficulty})`);
    
    // Create table document
    const table: TrainingTable = {
      tableId,
      tableName: config.tableName || `Training Table`,
      
      hostId: config.hostId,
      hostName: config.hostName,
      
      status: 'waiting',
      isPrivate: true,
      currency: 'playcoins',
      
      smallBlind: config.smallBlind || DEFAULT_CONFIG.smallBlind,
      bigBlind: config.bigBlind || DEFAULT_CONFIG.bigBlind,
      startingStack: config.startingStack || DEFAULT_CONFIG.startingStack,
      maxPlayers: config.maxPlayers || DEFAULT_CONFIG.maxPlayers,
      
      botDifficulty: config.botDifficulty,
      bots,
      
      players: [config.hostId],
      playerNames: { [config.hostId]: config.hostName },
      
      inviteCode,
      invitedPlayers: [],

      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    // Attach the mini-game challenge config when present (omit entirely
    // otherwise - Firestore rejects undefined fields).
    if (config.challenge) {
      table.challenge = config.challenge;
    }
    
    // Save to Firestore. Latency-compensated on purpose: the local cache
    // applies the write instantly and the table page's subscription reads
    // it from that cache, so we do NOT block the UI on the server ack -
    // that ack is what made launches crawl on a busy emulator. If the
    // server ultimately rejects the write, the subscription delivers null
    // and the table page routes back to the training lobby.
    const tableRef = doc(db, TRAINING_TABLES_COLLECTION, tableId);
    setDoc(tableRef, table).catch(err =>
      console.error(`❌ [TrainingTable] Create write rejected for ${tableId}:`, err)
    );

    console.log(`✅ [TrainingTable] Created table ${tableId} with code ${inviteCode}`);
    return table;
  } catch (error) {
    console.error(`❌ [TrainingTable] Failed to create table:`, error);
    throw error;
  }
}

/**
 * Send an in-app + phone-push invite to a friend for a training table.
 * Writes to inviteNotifications, which the onTableInviteNotification Cloud
 * Function turns into an FCM push; the deep link auto-joins via the
 * invite code when tapped.
 */
export function sendTrainingInvite(params: {
  targetUserId: string;
  inviterId: string;
  inviterName: string;
  tableId: string;
  tableName: string;
  inviteCode: string;
}): void {
  addDoc(collection(db, 'inviteNotifications'), {
    ...params,
    kind: 'training',
    link: `/training?join=${params.inviteCode}`,
    createdAt: Timestamp.now(),
  }).catch(err => console.error('❌ [TrainingTable] Invite send failed:', err));
}

/**
 * Replace the table's bot roster (tournament-style refill when bots bust).
 * Latency-compensated like the other table writes.
 */
export function updateTrainingTableBots(tableId: string, bots: BotPlayer[]): void {
  const tableRef = doc(db, TRAINING_TABLES_COLLECTION, tableId);
  updateDoc(tableRef, { bots, updatedAt: Timestamp.now() }).catch(err =>
    console.error(`❌ [TrainingTable] Bot roster write rejected for ${tableId}:`, err)
  );
}

// ============================================================================
// TABLE RETRIEVAL
// ============================================================================

/**
 * Get a training table by ID
 */
export async function getTrainingTable(tableId: string): Promise<TrainingTable | null> {
  try {
    const tableRef = doc(db, TRAINING_TABLES_COLLECTION, tableId);
    const tableSnap = await getDoc(tableRef);
    
    if (!tableSnap.exists()) {
      return null;
    }
    
    return tableSnap.data() as TrainingTable;
  } catch (error) {
    console.error(`❌ [TrainingTable] Failed to get table:`, error);
    return null;
  }
}

/**
 * Get a training table by invite code
 */
export async function getTrainingTableByCode(inviteCode: string): Promise<TrainingTable | null> {
  try {
    const tablesRef = collection(db, TRAINING_TABLES_COLLECTION);
    const q = query(tablesRef, where('inviteCode', '==', inviteCode.toUpperCase()));
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return null;
    }
    
    return snapshot.docs[0].data() as TrainingTable;
  } catch (error) {
    console.error(`❌ [TrainingTable] Failed to get table by code:`, error);
    return null;
  }
}

/**
 * Get all training tables for a user (as host or player)
 */
export async function getUserTrainingTables(uid: string): Promise<TrainingTable[]> {
  try {
    const tablesRef = collection(db, TRAINING_TABLES_COLLECTION);
    
    // Get tables where user is host
    const hostQuery = query(tablesRef, where('hostId', '==', uid));
    const hostSnapshot = await getDocs(hostQuery);
    
    // Get tables where user is player
    const playerQuery = query(tablesRef, where('players', 'array-contains', uid));
    const playerSnapshot = await getDocs(playerQuery);
    
    // Combine and dedupe
    const tables = new Map<string, TrainingTable>();
    
    hostSnapshot.docs.forEach(docSnap => {
      tables.set(docSnap.id, docSnap.data() as TrainingTable);
    });
    
    playerSnapshot.docs.forEach(docSnap => {
      tables.set(docSnap.id, docSnap.data() as TrainingTable);
    });
    
    return Array.from(tables.values());
  } catch (error) {
    console.error(`❌ [TrainingTable] Failed to get user tables:`, error);
    return [];
  }
}

/**
 * Subscribe to training table updates
 */
export function subscribeToTrainingTable(
  tableId: string,
  callback: (table: TrainingTable | null) => void
): () => void {
  const tableRef = doc(db, TRAINING_TABLES_COLLECTION, tableId);
  
  const unsubscribe = onSnapshot(tableRef, (snapshot) => {
    if (snapshot.exists()) {
      callback(snapshot.data() as TrainingTable);
    } else {
      callback(null);
    }
  }, (error) => {
    console.error(`❌ [TrainingTable] Subscription error:`, error);
    callback(null);
  });
  
  return unsubscribe;
}

// ============================================================================
// PLAYER MANAGEMENT
// ============================================================================

/**
 * Join a training table via invite code
 */
export async function joinTrainingTable(
  inviteCode: string,
  uid: string,
  username: string
): Promise<{ success: boolean; table?: TrainingTable; error?: string }> {
  try {
    const table = await getTrainingTableByCode(inviteCode);
    
    if (!table) {
      return { success: false, error: 'Invalid invite code' };
    }
    
    // Rejoin is SUCCESS, not an error: a friend who navigated away and
    // re-enters the code should land back at the table, whatever its status.
    if (table.players.includes(uid)) {
      return { success: true, table };
    }

    if (table.status !== 'waiting') {
      return { success: false, error: 'Game already in progress - ask the host for a fresh table' };
    }
    
    const totalPlayers = table.players.length + table.bots.length;
    if (totalPlayers >= table.maxPlayers) {
      return { success: false, error: 'Table is full' };
    }
    
    // Add player to table
    const tableRef = doc(db, TRAINING_TABLES_COLLECTION, table.tableId);
    await updateDoc(tableRef, {
      players: arrayUnion(uid),
      [`playerNames.${uid}`]: username,
      updatedAt: Timestamp.now(),
    });
    
    const updatedTable = await getTrainingTable(table.tableId);
    console.log(`✅ [TrainingTable] ${username} joined table ${table.tableId}`);
    
    return { success: true, table: updatedTable || table };
  } catch (error) {
    console.error(`❌ [TrainingTable] Failed to join table:`, error);
    return { success: false, error: 'Failed to join table' };
  }
}

/**
 * Record per-player challenge progress on the table doc (e.g. the steal
 * drill's preflop-win count). Chip goals live in gameState and survive
 * refresh for free; counter-based goals need this persistence so a player
 * who refreshes mid-run (or a co-op joiner) doesn't lose credited progress.
 */
export async function recordChallengeProgress(
  tableId: string,
  uid: string,
  progress: { preflopWins?: number }
): Promise<void> {
  try {
    const tableRef = doc(db, TRAINING_TABLES_COLLECTION, tableId);
    const update: Record<string, any> = { updatedAt: Timestamp.now() };
    if (progress.preflopWins !== undefined) {
      update[`challengeProgress.${uid}.preflopWins`] = progress.preflopWins;
    }
    await updateDoc(tableRef, update);
  } catch (error) {
    console.warn('[TrainingTable] challenge progress write failed:', error);
  }
}

/**
 * Leave a training table
 */
export async function leaveTrainingTable(
  tableId: string,
  uid: string,
  preloadedTable?: TrainingTable
): Promise<{ success: boolean; report?: TrainingReport }> {
  try {
    // The page passes its subscribed table; a fresh server getDoc here
    // queues behind every pending latency-compensated game write, which
    // is exactly how the Leave buttons got stuck spinning.
    const table = preloadedTable || await getTrainingTable(tableId);

    if (!table) {
      return { success: false };
    }

    // End and analyze the training session (local CPU - instant). The
    // report SAVE is latency-compensated: the report object goes back to
    // the caller immediately; the write lands in the background.
    let report: TrainingReport | undefined;
    const session = trainingDecisionLogger.endSession();

    if (session && session.decisions.length > 0) {
      console.log(`📊 [TrainingTable] Analyzing session with ${session.decisions.length} decisions`);
      report = analyzeTrainingSession(session);
      saveTrainingReport(report).catch(err =>
        console.error(`❌ [TrainingTable] Report save failed:`, err)
      );
    }

    // Remove player from table (or tear it down if the host leaves) -
    // latency-compensated like every other table write.
    const tableRef = doc(db, TRAINING_TABLES_COLLECTION, tableId);

    if (table.hostId === uid) {
      deleteDoc(tableRef).catch(err =>
        console.error(`❌ [TrainingTable] Table delete failed:`, err)
      );
      console.log(`🗑️ [TrainingTable] Host left, deleting table ${tableId}`);
    } else {
      updateDoc(tableRef, {
        players: arrayRemove(uid),
        updatedAt: Timestamp.now(),
      }).catch(err =>
        console.error(`❌ [TrainingTable] Leave write failed:`, err)
      );
      console.log(`👋 [TrainingTable] ${uid} leaving table ${tableId}`);
    }

    return { success: true, report };
  } catch (error) {
    console.error(`❌ [TrainingTable] Failed to leave table:`, error);
    return { success: false };
  }
}

/**
 * Invite a player to the training table
 */
export async function inviteToTrainingTable(
  tableId: string,
  inviterId: string,
  inviteeId: string
): Promise<boolean> {
  try {
    const table = await getTrainingTable(tableId);
    
    if (!table || table.hostId !== inviterId) {
      return false;
    }
    
    const tableRef = doc(db, TRAINING_TABLES_COLLECTION, tableId);
    await updateDoc(tableRef, {
      invitedPlayers: arrayUnion(inviteeId),
      updatedAt: Timestamp.now(),
    });
    
    console.log(`📨 [TrainingTable] Invited ${inviteeId} to table ${tableId}`);
    return true;
  } catch (error) {
    console.error(`❌ [TrainingTable] Failed to invite:`, error);
    return false;
  }
}

// ============================================================================
// BOT MANAGEMENT
// ============================================================================

/**
 * Add a bot to the table
 */
export async function addBotToTable(
  tableId: string,
  difficulty: BotDifficulty
): Promise<BotPlayer | null> {
  try {
    const table = await getTrainingTable(tableId);
    
    if (!table) {
      return null;
    }
    
    const totalPlayers = table.players.length + table.bots.length;
    if (totalPlayers >= table.maxPlayers) {
      console.warn(`⚠️ [TrainingTable] Table is full`);
      return null;
    }
    
    const bot = createBot(difficulty);
    
    const tableRef = doc(db, TRAINING_TABLES_COLLECTION, tableId);
    await updateDoc(tableRef, {
      bots: arrayUnion(bot),
      updatedAt: Timestamp.now(),
    });
    
    console.log(`🤖 [TrainingTable] Added ${bot.displayName} (${difficulty}) to table`);
    return bot;
  } catch (error) {
    console.error(`❌ [TrainingTable] Failed to add bot:`, error);
    return null;
  }
}

/**
 * Remove a bot from the table
 */
export async function removeBotFromTable(
  tableId: string,
  botId: string
): Promise<boolean> {
  try {
    const table = await getTrainingTable(tableId);
    
    if (!table) {
      return false;
    }
    
    const bot = table.bots.find(b => b.uid === botId);
    if (!bot) {
      return false;
    }
    
    const tableRef = doc(db, TRAINING_TABLES_COLLECTION, tableId);
    await updateDoc(tableRef, {
      bots: arrayRemove(bot),
      updatedAt: Timestamp.now(),
    });
    
    console.log(`🤖 [TrainingTable] Removed bot ${botId} from table`);
    return true;
  } catch (error) {
    console.error(`❌ [TrainingTable] Failed to remove bot:`, error);
    return false;
  }
}

/**
 * Update bot difficulty
 */
export async function updateBotDifficulty(
  tableId: string,
  newDifficulty: BotDifficulty | 'mixed'
): Promise<boolean> {
  try {
    const table = await getTrainingTable(tableId);
    
    if (!table) {
      return false;
    }
    
    // Create new bots with new difficulty
    const newBots = createBots(table.bots.length, newDifficulty);
    
    const tableRef = doc(db, TRAINING_TABLES_COLLECTION, tableId);
    await updateDoc(tableRef, {
      bots: newBots,
      botDifficulty: newDifficulty,
      updatedAt: Timestamp.now(),
    });
    
    console.log(`🤖 [TrainingTable] Updated bots to ${newDifficulty}`);
    return true;
  } catch (error) {
    console.error(`❌ [TrainingTable] Failed to update bot difficulty:`, error);
    return false;
  }
}

// ============================================================================
// GAME STATE MANAGEMENT
// ============================================================================

/**
 * Start the training game
 */
export async function startTrainingGame(
  tableId: string,
  hostId?: string,
  preloadedTable?: TrainingTable
): Promise<{ success: boolean; error?: string }> {
  try {
    // The table page already holds the table from its live subscription -
    // use it when provided. A fresh server getDoc here queues behind the
    // (deliberately un-awaited) create ack on a busy emulator, which is
    // exactly the Deal Me In freeze.
    const table = preloadedTable || await getTrainingTable(tableId);

    if (!table) {
      return { success: false, error: 'Table not found' };
    }
    
    if (table.status !== 'waiting') {
      return { success: false, error: 'Game already started' };
    }
    
    const totalPlayers = table.players.length + table.bots.length;
    if (totalPlayers < 2) {
      return { success: false, error: 'Need at least 2 players' };
    }
    
    // Build player array for game engine
    // Humans first, then bots. Mini-game challenges use asymmetric stacks
    // (short hero vs full bots) - the handicap IS the challenge.
    const heroChips = table.challenge?.heroChips ?? table.startingStack * table.bigBlind;
    const botChips = table.challenge?.botChips ?? table.startingStack * table.bigBlind;
    const allPlayers: { id: string; chips: number; seat?: number }[] = [];

    // Add human players
    table.players.forEach((odwinId, idx) => {
      allPlayers.push({
        id: odwinId,
        chips: heroChips,
        seat: idx,
      });
    });

    // Add bots
    table.bots.forEach((bot, idx) => {
      allPlayers.push({
        id: bot.uid,
        chips: botChips,
        seat: table.players.length + idx,
      });
    });
    
    // Import gameEngine dynamically to avoid circular deps
    const { default: gameEngine } = await import('../gameEngine');
    
    // Initialize game state
    const gameState = gameEngine.initializeGame(
      allPlayers,
      table.smallBlind,
      table.bigBlind,
      tableId
    );

    // 🧮 Chip-conservation baseline: stacks + pot must equal this at every
    // moment of every hand. The page audits against it and logs any drift.
    (gameState as any).chipTotal = allPlayers.reduce((s, p) => s + (p.chips || 0), 0);
    
    // Update table with status and game state. Latency-compensated like the
    // create: the local cache applies this instantly (subscription flips to
    // 'playing' and the deal begins) - do NOT block the UI on the server ack.
    const tableRef = doc(db, TRAINING_TABLES_COLLECTION, tableId);
    updateDoc(tableRef, {
      status: 'playing',
      gameState,
      updatedAt: Timestamp.now(),
    }).catch(err => console.error(`❌ [TrainingTable] Start write rejected for ${tableId}:`, err));

    console.log(`🎮 [TrainingTable] Started game at table ${tableId}`);
    return { success: true };
  } catch (error) {
    console.error(`❌ [TrainingTable] Failed to start game:`, error);
    return { success: false, error: 'Failed to start game' };
  }
}

/**
 * Remove undefined values from an object recursively
 * Firestore does not accept undefined values
 */
function removeUndefinedValues(obj: any): any {
  if (obj === null || obj === undefined) {
    return null;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefinedValues(item));
  }
  
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const key of Object.keys(obj)) {
      const value = obj[key];
      if (value !== undefined) {
        cleaned[key] = removeUndefinedValues(value);
      }
    }
    return cleaned;
  }
  
  return obj;
}

/**
 * Update the game state for a training table
 */
export async function updateTrainingGameState(
  tableId: string,
  gameState: any
): Promise<boolean> {
  try {
    // Clean undefined values - Firestore doesn't accept them
    const cleanedGameState = removeUndefinedValues(gameState);

    // Latency-compensated like every other table write: the local cache
    // applies this instantly (the page's own subscription re-renders from
    // it), and Firestore preserves per-client write order. Awaiting the
    // server ack here made every action - and especially the next-hand
    // deal - hang for as long as the backend felt like taking.
    const tableRef = doc(db, TRAINING_TABLES_COLLECTION, tableId);
    updateDoc(tableRef, {
      gameState: cleanedGameState,
      updatedAt: Timestamp.now(),
    }).catch(err =>
      console.error(`❌ [TrainingTable] Game state write rejected for ${tableId}:`, err)
    );
    return true;
  } catch (error) {
    console.error(`❌ [TrainingTable] Failed to update game state:`, error);
    return false;
  }
}

/**
 * End the training game
 */
export async function endTrainingGame(
  tableId: string
): Promise<TrainingReport | null> {
  try {
    const table = await getTrainingTable(tableId);
    
    if (!table) {
      return null;
    }
    
    // End and analyze training session
    const session = trainingDecisionLogger.endSession();
    let report: TrainingReport | null = null;
    
    if (session && session.decisions.length > 0) {
      report = analyzeTrainingSession(session);
      await saveTrainingReport(report);
    }
    
    // Update table status
    const tableRef = doc(db, TRAINING_TABLES_COLLECTION, tableId);
    await updateDoc(tableRef, {
      status: 'ended',
      updatedAt: Timestamp.now(),
    });
    
    console.log(`🏁 [TrainingTable] Ended game at table ${tableId}`);
    return report;
  } catch (error) {
    console.error(`❌ [TrainingTable] Failed to end game:`, error);
    return null;
  }
}

/**
 * Close a batch of the user's open training tables in one go: tables they
 * host are deleted, tables they joined get their seat removed. Used by the
 * lobby's "Close all" and to tidy up before a fresh practice game so old
 * solo tables do not pile up as "still open" banners.
 */
export async function closeTrainingTables(
  uid: string,
  tables: TrainingTable[],
): Promise<{ closed: number; failed: number }> {
  let closed = 0;
  let failed = 0;
  await Promise.all(tables.map(async (table) => {
    try {
      const tableRef = doc(db, TRAINING_TABLES_COLLECTION, table.tableId);
      if (table.hostId === uid) await deleteDoc(tableRef);
      else await updateDoc(tableRef, { players: arrayRemove(uid), updatedAt: Timestamp.now() });
      closed += 1;
    } catch (error) {
      failed += 1;
      console.error(`❌ [TrainingTable] Failed to close table ${table.tableId}:`, error);
    }
  }));
  return { closed, failed };
}

/**
 * Delete a training table
 */
export async function deleteTrainingTable(tableId: string): Promise<boolean> {
  try {
    const tableRef = doc(db, TRAINING_TABLES_COLLECTION, tableId);
    await deleteDoc(tableRef);
    
    console.log(`🗑️ [TrainingTable] Deleted table ${tableId}`);
    return true;
  } catch (error) {
    console.error(`❌ [TrainingTable] Failed to delete table:`, error);
    return false;
  }
}

// ============================================================================
// UTILITY
// ============================================================================

/**
 * Get all players at a training table (humans + bots)
 */
export function getAllTablePlayers(table: TrainingTable): TrainingTablePlayer[] {
  const players: TrainingTablePlayer[] = [];
  let seatIndex = 0;
  
  // Add human players
  for (const uid of table.players) {
    players.push({
      uid,
      username: table.playerNames[uid] || 'Player',
      isBot: false,
      seatIndex: seatIndex++,
      chips: table.startingStack,
      isActive: true,
    });
  }
  
  // Add bots
  for (const bot of table.bots) {
    players.push({
      uid: bot.uid,
      username: bot.displayName,
      isBot: true,
      botDifficulty: bot.difficulty,
      seatIndex: seatIndex++,
      chips: table.startingStack,
      isActive: true,
    });
  }
  
  return players;
}

// ============================================================================
// EXPORT
// ============================================================================

export default {
  createTrainingTable,
  getTrainingTable,
  getTrainingTableByCode,
  getUserTrainingTables,
  subscribeToTrainingTable,
  joinTrainingTable,
  leaveTrainingTable,
  inviteToTrainingTable,
  addBotToTable,
  removeBotFromTable,
  updateBotDifficulty,
  startTrainingGame,
  endTrainingGame,
  deleteTrainingTable,
  getAllTablePlayers,
};